// eyeq — план поставки и техническая перепроверка перед сборкой (ADR-0092).
//
// Поставка собирается ПЛАНОМ с капом: кандидаты через гейт отгрузки,
// участники вперёд, остаток ёмкости ячеек. Перед сборкой каждый кадр плана
// перепроверяется ПО БАЙТАМ — не по индексу: заказчик вернул кадры, у
// которых в JSON стояла Польша, а в GPS — Чехия. Правила перепроверки —
// чистая функция здесь, чтобы воркер и локальные скрипты судили одинаково.
import { z } from 'zod';
import { EYEQ_LOCALES } from './locales';
import { EYEQ_MIN_PIXELS, EYEQ_DELIVERY_MAX_AGE_DAYS } from './upload';
import { eyeqCapturedTs } from './gates';

/** Кап одной поставки — договорённость с приёмкой заказчика (2026-09-08). */
export const EYEQ_SHIPMENT_CAP = 3000;

export const EYEQ_RECHECK_ISSUES = [
  'no_bytes',              // объект в R2 не найден или пуст
  'sha_mismatch',          // хэш байтов не совпал с записанным в каталоге
  'media_mismatch',        // формат/размеры по байтам не совпали с каталогом
  'below_min_pixels',      // < 12 МП по байтам
  'no_exif',               // EXIF не читается
  'no_device_exif',        // нет make/model
  'no_capture_date',       // нет DateTimeOriginal
  'stale',                 // старше EYEQ_DELIVERY_MAX_AGE_DAYS на дату перепроверки
  'no_gps',                // GPS в EXIF отсутствует
  'geo_unknown',           // точка есть, но ни одна страна плана её не содержит
  'geo_country_mismatch',  // страна по GPS ≠ страна локали кадра
  'meta_country_mismatch', // страна в мете ≠ страна локали (написание/значение)
] as const;
export const EyeqRecheckIssueSchema = z.enum(EYEQ_RECHECK_ISSUES);
export type EyeqRecheckIssue = z.infer<typeof EyeqRecheckIssueSchema>;

export interface EyeqRecheckInput {
  locale: string;
  meta_country: string;
  stored_sha256: string;
  stored_bytes: number | null;
  stored_width: number | null;
  stored_height: number | null;
  /** Снято с байтов. */
  bytes: number;
  sha256: string;
  mime: string;
  width: number | null;
  height: number | null;
  exif_ok: boolean;
  make: string;
  model: string;
  captured_at: string;
  gps_present: boolean;
  /** Страна по координатам (уже вычислена); '' — нет точки или не опознана. */
  geo_country: string;
  now: Date;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const COUNTRY_ALIASES: Record<string, string> = {
  'uae': 'united arab emirates', 'u.a.e.': 'united arab emirates', 'emirates': 'united arab emirates',
  'czechia': 'czech republic', 'korea': 'south korea', 'republic of korea': 'south korea',
  'ksa': 'saudi arabia', 'kingdom of saudi arabia': 'saudi arabia', 'hong kong sar': 'hong kong', 'taiwan, province of china': 'taiwan',
};
export const eyeqCountryKey = (s: string): string => { const n = norm(s); return COUNTRY_ALIASES[n] ?? n; };

/** Страна локали по реестру; '' — локали нет в плане. */
export function eyeqLocaleCountry(locale: string): string {
  return EYEQ_LOCALES.find((l) => l.locale === locale)?.country ?? '';
}

/** Все причины, по порядку дешевизны. Пусто — кадр прошёл перепроверку. */
export function eyeqRecheckIssues(i: EyeqRecheckInput): EyeqRecheckIssue[] {
  const out: EyeqRecheckIssue[] = [];
  if (!i.bytes) return ['no_bytes'];
  if (i.stored_sha256 && i.sha256 && i.stored_sha256 !== i.sha256) out.push('sha_mismatch');
  if ((i.stored_width && i.width && i.stored_width !== i.width) || (i.stored_height && i.height && i.stored_height !== i.height)) out.push('media_mismatch');
  if (i.width != null && i.height != null && i.width * i.height < EYEQ_MIN_PIXELS) out.push('below_min_pixels');
  if (!i.exif_ok) out.push('no_exif');
  if (!(i.make || '').trim() || !(i.model || '').trim()) out.push('no_device_exif');
  const ts = eyeqCapturedTs(i.captured_at); // секунды UNIX (как captured_ts в каталоге)
  if (ts == null) out.push('no_capture_date');
  else if ((i.now.getTime() / 1000 - ts) / 86_400 > EYEQ_DELIVERY_MAX_AGE_DAYS) out.push('stale');
  const want = eyeqLocaleCountry(i.locale);
  if (!i.gps_present) out.push('no_gps');
  else if (!i.geo_country) out.push('geo_unknown');
  else if (want && eyeqCountryKey(i.geo_country) !== eyeqCountryKey(want)) out.push('geo_country_mismatch');
  if (want && i.meta_country && eyeqCountryKey(i.meta_country) !== eyeqCountryKey(want)) out.push('meta_country_mismatch');
  return out;
}

// ── API ──────────────────────────────────────────────────────────────────────
export const EyeqShipPlanCreateReqSchema = z.object({
  actor: z.string().min(1).max(80),
  cap: z.number().int().positive().max(10000).default(EYEQ_SHIPMENT_CAP),
  /** all — любые ячейки; nourl — только без обязательной ссылки; url — только с ней;
   *  url_pending — ячейки со ссылкой, но ссылка ещё не требуется (план ПРОВЕРКИ
   *  перед подбором ссылок, не отгрузки; кап до 10 000). */
  link_cells: z.enum(['all', 'nourl', 'url', 'url_pending']).default('all'),
  locales: z.array(z.string().min(2).max(8)).max(40).default([]),
  /** Фильтр по категориям (имена из схемы), пусто = все. */
  categories: z.array(z.string().min(1).max(80)).max(40).default([]),
  sub_categories: z.array(z.string().min(1).max(80)).max(60).default([]),
  note: z.string().max(300).default(''),
  /** accepted — только принятые QA (план отгрузки); any — и ожидающие вердикта
   *  (план ПРОВЕРКИ по байтам всего категоризованного пула, 2026-09-10). Сборка
   *  поставки такие кадры всё равно не пропустит: гейт `not_qa_accepted` стоит
   *  в `delivery/ship`. */
  qa_state: z.enum(['accepted', 'any']).default('accepted'),
}).superRefine((v, ctx) => {
  if (v.link_cells !== 'url_pending' && v.cap > EYEQ_SHIPMENT_CAP) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `cap > ${EYEQ_SHIPMENT_CAP}`, path: ['cap'] });
});
export type EyeqShipPlanCreateReq = z.infer<typeof EyeqShipPlanCreateReqSchema>;

/** Выборка на смысл (владелец, 2026-09-08): 10 % кадров каждой страны плана,
 *  минимум EYEQ_SAMPLE_MIN; страна с долей провалов выше EYEQ_SAMPLE_HOLD_RATE
 *  держится на ручную проверку целиком. */
export const EYEQ_SAMPLE_SHARE = 0.10;
export const EYEQ_SAMPLE_MIN = 5;
export const EYEQ_SAMPLE_HOLD_RATE = 0.15;
export const EYEQ_SAMPLE_VERDICTS = ['ok', 'category_mismatch', 'description_mismatch', 'error'] as const;

/** Удерживать ли страну плана на ручную проверку: правило ЧАСТИЧНОЙ выборки.
 *  Если выборка назначена на всех прошедших (sampled ≥ passed), каждый провал уже
 *  исключён поимённо и удержание не нужно — даже когда часть кадров закончилась
 *  ошибкой модели и sample_done < passed (2026-09-10: пять таких кадров держали
 *  Японию и Чехию целиком). */
export function eyeqLocaleHold(a: { sampled: number; sample_done: number; sample_failed: number; passed: number }): boolean {
  if (a.sample_done <= 0 || a.sampled >= a.passed) return false;
  return a.sample_failed / a.sample_done > EYEQ_SAMPLE_HOLD_RATE;
}
export const EyeqSampleVerdictSchema = z.enum(EYEQ_SAMPLE_VERDICTS);

export const EyeqShipPlanSampleReqSchema = z.object({
  plan_id: z.string().min(1),
  share: z.number().min(0.01).max(1).default(EYEQ_SAMPLE_SHARE),
  min_per_locale: z.number().int().min(1).max(100).default(EYEQ_SAMPLE_MIN),
  seed: z.string().max(40).default(''),
});
export type EyeqShipPlanSampleReq = z.infer<typeof EyeqShipPlanSampleReqSchema>;

/** Закрыть план (2026-09-10): кадры перестают держать ёмкость ячеек и снова доступны
 *  другим планам. Единственный переход — в `cancelled`; 'built' ставит сборка. Нужен
 *  для планов ПРОВЕРКИ (url_pending / qa_state=any), у которых нет сборки. */
export const EyeqShipPlanCloseReqSchema = z.object({
  plan_id: z.string().min(1),
  actor: z.string().min(1).max(80),
  note: z.string().max(300).default(''),
});
export type EyeqShipPlanCloseReq = z.infer<typeof EyeqShipPlanCloseReqSchema>;
export const EyeqShipPlanCloseResSchema = z.object({ plan_id: z.string(), status: z.string(), released: z.number().int() });
export type EyeqShipPlanCloseRes = z.infer<typeof EyeqShipPlanCloseResSchema>;

export const EyeqShipPlanSampleResultReqSchema = z.object({
  plan_id: z.string().min(1),
  rows: z.array(z.object({
    asset_id: z.string().min(1), verdict: EyeqSampleVerdictSchema, why: z.string().max(400).default(''),
  })).min(1).max(50),
});
export type EyeqShipPlanSampleResultReq = z.infer<typeof EyeqShipPlanSampleResultReqSchema>;

export const EyeqShipPlanLocaleRowSchema = z.object({
  locale: z.string(), planned: z.number().int(), rechecked: z.number().int(), passed: z.number().int(), failed: z.number().int(),
  sampled: z.number().int(), sample_done: z.number().int(), sample_failed: z.number().int(),
  /** Доля провалов выборки выше порога — страна держится на ручную проверку. */
  hold: z.boolean(),
});
export const EyeqShipPlanReportSchema = z.object({
  plan_id: z.string(), status: z.string(), cap: z.number().int(), link_cells: z.string(), created_at: z.string(), actor: z.string(), note: z.string(),
  candidates: z.number().int(),      // прошли гейт и поместились в ёмкость до капа
  gate_failed: z.record(z.string(), z.number().int()), // причины гейта у отброшенных кандидатов (до капа)
  planned: z.number().int(), rechecked: z.number().int(), passed: z.number().int(), failed: z.number().int(),
  issues: z.record(z.string(), z.number().int()),
  sampled: z.number().int(), sample_done: z.number().int(), sample_failed: z.number().int(),
  held_locales: z.array(z.string()),
  /** Готово к сборке: перепроверка пройдена, выборка не провалена, страна не удержана. */
  ready: z.number().int(),
  locales: z.array(EyeqShipPlanLocaleRowSchema),
});
export type EyeqShipPlanReport = z.infer<typeof EyeqShipPlanReportSchema>;

export const EyeqShipPlanItemsResSchema = z.object({
  plan_id: z.string(),
  items: z.array(z.object({
    asset_id: z.string(), locale: z.string(), ext: z.string(), bytes: z.number().int().nullable(),
    /** Только для kind=sample: что проверять на смысл. */
    category: z.string().optional(), sub_category: z.string().optional(), subject_name: z.string().optional(), disambiguation_details: z.string().optional(),
  })),
  next: z.string().nullable(),
});
export type EyeqShipPlanItemsRes = z.infer<typeof EyeqShipPlanItemsResSchema>;

/** PUT /delivery/plan/recheck — тело: голова файла (≤ 2 МБ), в заголовках
 *  plan_id, asset_id, sha256 всего файла и его размер (их считает машина). */
export const EyeqShipPlanRecheckResSchema = z.object({
  asset_id: z.string(), issues: z.array(EyeqRecheckIssueSchema),
  geo_country: z.string(), locale_country: z.string(), meta_country: z.string(), captured_at: z.string(),
});
export type EyeqShipPlanRecheckRes = z.infer<typeof EyeqShipPlanRecheckResSchema>;
